/**
 * Store Offers hooks — TanStack Query wrappers around `RetailerOffersApi`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SaveOfferToHouseholdProductsRequest,
  SearchRetailerOffersOptions,
} from '../../types/requests';
import { useApi } from './context';
import { keys } from './keys';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

// T26.1 — when the server reports it enqueued an offer-sync job for an empty
// linked store (`syncTriggered`), poll a few times so the offers the worker
// writes appear without a manual refresh. Bounded so we never poll forever: poll
// every 2s for at most this many ticks, then stop. Once offers land (or the
// store turns out to legitimately have none and no sync was triggered), polling
// stops on its own.
const SYNC_POLL_INTERVAL_MS = 2_000;
const SYNC_POLL_MAX_TICKS = 6; // ~12s total — covers a stub adapter run + write.

export function useRetailerOffers(storeId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: storeId ? keys.RetailerOffers.byStore(storeId) : ['retailer-offers', 'disabled'],
    queryFn: () => api.RetailerOffers.fetchByStore(storeId as string),
    enabled: Boolean(storeId),
    // Keep refetching while a sync is in flight and no offers have arrived yet,
    // up to the tick cap. `query.state.dataUpdateCount` advances on every
    // successful fetch, so it doubles as the tick counter.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const stillEmpty = data.offers.length === 0;
      const syncing = data.syncTriggered === true;
      if (stillEmpty && syncing && query.state.dataUpdateCount < SYNC_POLL_MAX_TICKS) {
        return SYNC_POLL_INTERVAL_MS;
      }
      return false;
    },
  });
}

export function useRetailerOffer(
  storeId: string | undefined,
  offerId: string | undefined
) {
  const api = useApi();
  const enabled = Boolean(storeId && offerId);
  return useQuery({
    queryKey: enabled
      ? keys.RetailerOffers.one(storeId as string, offerId as string)
      : ['retailer-offers', 'one', 'disabled'],
    queryFn: () => api.RetailerOffers.fetch(storeId as string, offerId as string),
    enabled,
  });
}

export function useSearchRetailerOffers(
  query: string,
  options?: SearchRetailerOffersOptions
) {
  const api = useApi();
  return useQuery({
    queryKey: keys.RetailerOffers.search(query),
    queryFn: () => api.RetailerOffers.search(query, options),
    enabled: query.length > 0,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

// T12.1.2 — `useCreateRetailerOffer` / `useUpdateRetailerOffer` /
// `useDeleteRetailerOffer` removed: the offer CRUD chain was dead (zero
// non-test callers). Offer writes flow through the sync engine's
// `upsertOfferPricing`.

export function useSaveOfferToHouseholdProducts() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveOfferToHouseholdProductsRequest) =>
      api.RetailerOffers.saveToHouseholdProducts(input),
    onSuccess: () => {
      // Affects both HouseholdProducts list and the linked offers for the new product.
      void qc.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
    },
  });
}
